import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Timer, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMinutes, relativeTime } from '../lib/format';
import { OVERTIME_STATUSES, overtimeTone, useWaitingOvertime, type Overtime, type OvertimeStatus } from '../lib/overtime';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

type Scope = 'mine' | 'review' | 'team';

/** "Waiting for the employee" — or, on one's own, "Waiting for your answer". */
export function overtimeStatusLabel(status: OvertimeStatus, own: boolean, t: (key: TranslationKey) => string): string {
  return own && status === 'APPROVED' ? t('overtime.statusMine.APPROVED') : t(`overtime.status.${status}` as TranslationKey);
}

/**
 * Route: `/overtime` — the overtime that reached me, to accept or decline; for an
 * administrator, what waits for approval; for a department's leader and an
 * administrator, what they sent and their people's (operator, 2026-10-21). A row
 * opens its overtime.
 */
export function OvertimePage() {
  const { t } = useI18n();
  const { can, ledDepartmentIds } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const waiting = useWaitingOvertime();
  const approver = can(P.OVERTIME_APPROVE);
  // A leader sends overtime for their departments' people; an administrator for anyone.
  const sender = approver || ledDepartmentIds.length > 0;
  const requested = params.get('tab') as Scope | null;
  const tab: Scope =
    requested === 'review' && approver
      ? 'review'
      : requested === 'team' && sender
        ? 'team'
        : requested === 'mine' || !sender
          ? 'mine'
          : 'team';

  return (
    <>
      <PageHeader
        title={t('overtime.title')}
        subtitle={t('overtime.subtitle')}
        action={
          sender && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/overtime/new')}>
              {t('overtime.new')}
            </Button>
          )
        }
      />
      {sender && (
        <div className="mb-5">
          <Tabs
            active={tab}
            onChange={(id) => setParams({ tab: id }, { replace: true })}
            tabs={[
              { id: 'team', label: t('overtime.team') },
              ...(approver ? [{ id: 'review', label: t('overtime.review'), count: waiting.review || undefined }] : []),
              { id: 'mine', label: t('overtime.mine'), count: waiting.answer || undefined },
            ]}
          />
        </div>
      )}
      <OvertimeList key={tab} scope={tab} />
    </>
  );
}

function OvertimeList({ scope }: { scope: Scope }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['overtime', 'list', scope, page, status, q],
    queryFn: () =>
      api.get<Paginated<Overtime>>('/overtime', { scope, page, pageSize: 25, status: status || undefined, q: q || undefined }),
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'accept' | 'cancel' }) =>
      api.post(`/overtime/${id}/${action}`, {}),
    onSuccess: (_result, { action }) => {
      toast.success(
        t(action === 'approve' ? 'overtime.approvedToast' : action === 'accept' ? 'overtime.acceptedToast' : 'overtime.cancelledToast'),
      );
      void queryClient.invalidateQueries({ queryKey: ['overtime'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const showEmployee = scope !== 'mine';

  return (
    <>
      {scope !== 'mine' && (
        <Card className="mb-4" padded={false}>
          <div className="grid gap-3 p-4 sm:grid-cols-3">
            <Input placeholder={t('common.search')} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            {scope === 'team' && (
              <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label={t('common.status')}>
                <option value="">{t('common.status')} — {t('common.all')}</option>
                {OVERTIME_STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`overtime.status.${s}` as TranslationKey)}</option>
                ))}
              </Select>
            )}
          </div>
        </Card>
      )}

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<Timer className="h-8 w-8" />}
            title={scope === 'review' ? t('overtime.nothingToReview') : t('overtime.none')}
            hint={scope === 'mine' ? t('overtime.noneMine') : scope === 'team' ? t('overtime.noneTeam') : ''}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  {showEmployee && <Th>{t('common.employee')}</Th>}
                  <Th>{t('overtime.day')}</Th>
                  <Th align="end">{t('overtime.upTo')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.reason')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <Tr key={r.id} onClick={() => navigate(`/overtime/${r.id}`)}>
                    {showEmployee && (
                      <Td>
                        <span className="block">{localized(r.user)}</span>
                        {r.department && <span className="text-caption text-faint">{localized(r.department)}</span>}
                      </Td>
                    )}
                    <Td className="tabular whitespace-nowrap text-muted">{formatDate(r.workDate, locale)}</Td>
                    <Td align="end" className="tabular whitespace-nowrap">
                      <span className="block">{formatMinutes(r.minutes, locale)}</span>
                      {r.status === 'ACCEPTED' && (
                        <span className="text-caption text-faint">
                          {r.paidMinutes == null
                            ? t('overtime.paysLater')
                            : t('overtime.pays', { time: formatMinutes(r.paidMinutes, locale) })}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={overtimeTone(r.status)}>{overtimeStatusLabel(r.status, scope === 'mine', t)}</Badge>
                      {r.requestedBy && scope !== 'mine' && (
                        <span className="mt-0.5 block text-caption text-faint">
                          {t('overtime.sentBy')}: {localized(r.requestedBy)}
                        </span>
                      )}
                    </Td>
                    <Td className="max-w-xs">
                      <span className="block truncate text-muted" title={r.reason}>{r.reason}</span>
                      {(r.answerNote || r.reviewNote) && (
                        <span className="block truncate text-caption text-faint" title={(r.answerNote || r.reviewNote) ?? undefined}>
                          “{r.answerNote || r.reviewNote}”
                        </span>
                      )}
                      {scope === 'review' && (
                        <span className="block text-caption text-faint">{relativeTime(r.createdAt, locale)}</span>
                      )}
                    </Td>
                    <Td align="end">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {r.can.decide && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Check className="h-3.5 w-3.5" />}
                              disabled={act.isPending}
                              onClick={() => act.mutate({ id: r.id, action: 'approve' })}
                            >
                              {t('common.approve')}
                            </Button>
                            <Button size="sm" variant="ghost" icon={<X className="h-3.5 w-3.5" />} onClick={() => navigate(`/overtime/${r.id}/reject`)}>
                              {t('common.reject')}
                            </Button>
                          </>
                        )}
                        {r.can.answer && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Check className="h-3.5 w-3.5" />}
                              disabled={act.isPending}
                              onClick={() => act.mutate({ id: r.id, action: 'accept' })}
                            >
                              {t('overtime.accept')}
                            </Button>
                            <Button size="sm" variant="ghost" icon={<X className="h-3.5 w-3.5" />} onClick={() => navigate(`/overtime/${r.id}/decline`)}>
                              {t('overtime.decline')}
                            </Button>
                          </>
                        )}
                        {r.can.cancel && (
                          <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: 'cancel' })}>
                            {t('overtime.cancel')}
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
